import { Dialog } from "primereact/dialog";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import {
    Dialog as MuiDialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button as MuiButton,
} from "@mui/material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import Langs from "../../app/lang/langs";
import addIcon from '../../assets/images/add.png'
import addIconFocus from '../../assets/images/addFocus.png'
import ImageButton from "../../components/buttons/ImageButton";
import AddTeamManagersModal from "./AddTeamManagersModal";
import { getTeamsManagers, setTeamManagers, removeTeamManager } from "../../components/teams/teamsSlice";
import { Col, Row } from "react-bootstrap";
import deleteIcon from "../../assets/images/delete.png";
import deleteIconFocus from "../../assets/images/delete-orange.png";
import { checkPermission, showErrorToast } from "../../app/utils/helpers";
import { config } from "../../app/utils/config";

export default function ManagersListModal(props) {

    const dispatch = useDispatch()
    const managersList = useSelector((state) => state.teams.teamManagers)
    const [addModalVisibility, setAddModalVisibility] = useState(false)
    const [managerToRemove, setManagerToRemove] = useState(null)
    const canEdit = checkPermission(config.scopes.clientsTeams, config.permissionTypes.edit)

    useEffect(() => {
        if (props && props.visible && props.idTeam) {
            dispatch(getTeamsManagers({ id_team: props.idTeam }))
        } else {
            dispatch(setTeamManagers([]))
        }
    }, [props])

    const handleRemoveManager = (manager) => {
        if (!props.idTeam || !manager.id_collab) return;
        setManagerToRemove(manager);
    };

    const confirmRemoveManager = async () => {
        const manager = managerToRemove;
        setManagerToRemove(null);
        if (!manager) return;

        const data = {
            id_team: props.idTeam,
            id_collab: manager.id_collab,
        };

        try {
            const response = await dispatch(removeTeamManager(data));

            if (response.payload.responseData && response.payload.responseData.status === 1) {
                const updatedManagers = managersList.filter((m) => m.id_collab !== manager.id_collab);
                dispatch(setTeamManagers(updatedManagers));
            } else {
                showErrorToast(response.payload?.message || "Failed to remove manager");
            }
        } catch (error) {
            console.error("Error removing manager:", error);
            showErrorToast("An error occurred while removing the manager");
        }
    };

    const modalHeader = () => {
        return (
            <div>
                <Row>
                    {canEdit && <Col md={1}>
                        <ImageButton
                            image={addIcon}
                            imageFocus={addIconFocus}
                            onClick={() => { setAddModalVisibility(true) }}
                            active={true}
                            tooltip={<Langs str="ADD_MANAGERS" />}
                            width={32} /></Col>}
                    <Col className="align-content-center">
                        {props.teamName}
                    </Col>
                </Row>
            </div>
        )
    }

    return (
        <Dialog
            visible={props.visible}
            style={{ width: 'fit-content', height: "100vh", maxHeight: "100%", marginRight: 0 }}
            onHide={() => { if (!props.visible) return; props.setVisible(false); }}
            position="right"
            draggable={false}
            dismissableMask={true}
            header={modalHeader}
        >
            <DataTable value={managersList}
                tableStyle={{ maxWidth: '30rem' }}
                scrollable
                scrollHeight="calc(85vh)"
                draggable={false}
            >
                <Column sortable field="prenom" header={<Langs str="prenom" />}></Column>
                <Column sortable field="nom" header={<Langs str="nom" />}></Column>
                <Column sortable style={{ minWidth: "18rem" }} field="email" header={<Langs str="email" />}></Column>
                <Column field="type" header={<Langs str="TYPE" />}></Column>
                {canEdit && <Column
                    field="actions"
                    body={(rowData) => (
                        <Col className="d-flex justify-content-center">
                            <ImageButton
                                image={deleteIcon}
                                imageFocus={deleteIconFocus}
                                onClick={() => handleRemoveManager(rowData)}
                                active={true}
                                tooltip={<Langs str="REMOVE_MANAGER" />}
                                width={20}
                            />
                        </Col>
                    )}
                />}
            </DataTable>
            <AddTeamManagersModal
                visible={addModalVisibility}
                setVisible={setAddModalVisibility}
                prevManagers={managersList}
                idTeam={props.idTeam}
            />
            <MuiDialog
                open={!!managerToRemove}
                onClose={() => setManagerToRemove(null)}
                maxWidth="xs"
                fullWidth
                sx={{ zIndex: 1500 }}
                PaperProps={{
                    sx: {
                        borderRadius: '16px',
                        backgroundColor: '#fff',
                        boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        fontWeight: 700,
                        color: '#1a1a1a',
                        pb: 1,
                    }}
                >
                    <WarningAmberRoundedIcon sx={{ color: '#FF9015', fontSize: 32 }} />
                    <Langs str="REMOVE_MANAGER" />
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: 'rgba(0,0,0,0.75)', fontSize: '0.95rem' }}>
                        <Langs str="REMOVE_MANAGER_CONFIRM_MESSAGE" />
                        {managerToRemove && ` (${(managerToRemove.prenom || '').trim()} ${(managerToRemove.nom || '').trim()})`}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <MuiButton
                        onClick={() => setManagerToRemove(null)}
                        sx={{
                            textTransform: 'none',
                            color: 'rgba(0,0,0,0.7)',
                            borderRadius: '10px',
                            px: 2,
                        }}
                    >
                        <Langs str="cancel" />
                    </MuiButton>
                    <MuiButton
                        onClick={confirmRemoveManager}
                        variant="contained"
                        autoFocus
                        sx={{
                            textTransform: 'none',
                            backgroundColor: '#FF9015',
                            borderRadius: '10px',
                            px: 2.5,
                            boxShadow: '0 4px 12px rgba(255,144,21,0.3)',
                            '&:hover': { backgroundColor: '#e67e0a' },
                        }}
                    >
                        <Langs str="accept" />
                    </MuiButton>
                </DialogActions>
            </MuiDialog>
        </Dialog>
    )
}